// Importing necessary modules and hooks from React, Redux, Material-UI and local modules
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import {
    Grid,
    Button,
    Snackbar,
    Backdrop,
    Typography,
    Box
} from "@mui/material"
import Alert from "./Alert"
import UpdateGears from "../../resources/images/updateGears.png"
import RefreshIcon from "@mui/icons-material/Refresh"
import CloseIcon from "@mui/icons-material/Close"
import axios from "../../auth/axiosAuth"
import { AlertTextStyles, AlertTitleStyles, LogoStyles, LowerCaseButtonLeftStyles, LowerCaseButtonRightStyles } from "./styles/VersionAlertStyles"
import { APPLICATION_ID, appAdminBackendURL } from "../../config"

// Customizing styles for snackbar (alert component)
    const rootStyles = {
        width: "100%"
    }
    const alertStyles = {
        padding: (theme) => theme.spacing(2.5)
    }
    const backdropStyles = {
        zIndex: (theme) => theme.zIndex.snackbar + 4,
        color: "#fff",
        backgroundColor: "rgba(0, 0, 0, .8)"
    }
    const snackbarStyles = {
        height: "75%",
        zIndex: (theme) => theme.zIndex.snackbar + 10
    }


// Main component for displaying version alerts
function VersionAlert({ children }) {

    // Selecting logged-in user's auth status from Redux store
    const loggedIn = useSelector(
        (state) => state.auth.user && state.auth.user.auth
    )
    
    const authenticated = useSelector((state) => state.auth.authenticated)

    // Defining local state for version, latestVersion, alert open status, backdrop open status, and showAlertAgain status
    const [version, setVersion] = useState(null)
    const [latestVersion, setLatestVersion] = useState(null)
    const [open, setOpen] = useState(false)
    const [backdropOpen, setBackdropOpen] = useState(false)
    const [showAlertAgain, setShowAlertAgain] = useState(false)

    // useEffect to handle re-opening of the update notification after specific interval if the showAlertAgain flag is set
    useEffect(() => {
        if (showAlertAgain) {
            const timeoutId = setTimeout(() => {
                setOpen(true)
                setBackdropOpen(true)
                setShowAlertAgain(false)
            }, 5 * 60 * 1000) // 5 minutes in milliseconds

            return () => clearTimeout(timeoutId)
        }
    }, [showAlertAgain])

    // useEffect to initialize version state after user log in
    useEffect(() => {
        const initialVersion = async () => {
            if (loggedIn && !version && authenticated) {
                try {
                    const fetchedVersion = await getVersion(APPLICATION_ID)
                    setVersion(fetchedVersion)
                } catch (error) {
                    console.error(error)
                }
            }
            return () => {}
        }
        initialVersion()
    }, [loggedIn, version])

    // useEffect to periodically check for updates
    // useEffect(() => {
    //     const checkVersion = async () => {
    //         if (loggedIn && authenticated){
    //             try {
    //                 const fetchedVersion = await getVersion(APPLICATION_ID)
    //                 setLatestVersion(fetchedVersion)
    //             } catch (error) {
    //                 console.error(error)
    //             }
    //         }
            
    //     }

    //     checkVersion() // Run immediately on mount
    //     const intervalId = setInterval(checkVersion, 6000) // then every 1 minute

    //     return () => clearInterval(intervalId) // cleanup on unmount
    // }, [])

    // useEffect to show the alert if new version is detected
    useEffect(() => {
        if (version && latestVersion && version !== latestVersion) {
            setOpen(true)
            setBackdropOpen(true)
        }
    }, [latestVersion, version])

    // Function to remove 'v' from version string
    function removeV(word) {
        return word.replace(/v/g, "")
    }

    // Function to get the application version
    const getVersion = async (applicationID) => {
        const res = await axios.get(
            `${appAdminBackendURL}/application/${applicationID}`
        )
        let fetchedVersion = null
        if (res.data[0]) {
            fetchedVersion = `v${res.data[0].MajorVersion}.${res.data[0].MinorVersion}.${res.data[0].FixVersion}`
        }

        return fetchedVersion
    }

    // Handler function for closing the alert and scheduling it to show again
    const handleAlertDismiss = () => {
        setOpen(false)
        setBackdropOpen(false)
        setShowAlertAgain(true)
    }

    // Handler function for refreshing the page to update the app
    const handleRefresh = () => {
        window.location.reload()
    }

    // Render alert and backdrop along with children
    return (
        <>
            <Box sx={rootStyles}>
                <Snackbar
                    sx={snackbarStyles}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "center"
                    }}
                    open={open}
                    onClose={handleAlertDismiss}
                >
                    <div>
                    <Alert
                        severity="warning"
                        icon={false}
                        sx={alertStyles}
                    >
                        <Grid
                            container
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid item align="center">
                                <img
                                    alt="Update Icon"
                                    src={UpdateGears}
                                    sx={LogoStyles}
                                />
                            </Grid>
                            <Grid item align="center">
                                <Typography
                                    variant="h6"
                                    sx={AlertTitleStyles}
                                >
                                    {`New Update Available!`}
                                </Typography>
                            </Grid>
                            <Grid item align="center">
                                <Typography
                                    variant="body1"
                                    sx={AlertTextStyles}
                                >
                                    {`Version ${
                                        latestVersion
                                            ? removeV(latestVersion)
                                            : ""
                                    }  is now ready for your usage. Press 'Update' to enhance your experience.`}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={AlertTextStyles}
                                >
                                    If you choose to 'Dismiss', you will be
                                    reminded again in 5 minutes.
                                </Typography>
                            </Grid>
                            <Grid
                                container
                                direction="row"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Button
                                    startIcon={<RefreshIcon />}
                                    color="inherit"
                                    onClick={handleRefresh}
                                    sx={LowerCaseButtonLeftStyles}
                                    variant="contained"
                                >
                                    Update
                                </Button>
                                <Button
                                    endIcon={<CloseIcon />}
                                    color="inherit"
                                    onClick={handleAlertDismiss}
                                    sx={LowerCaseButtonRightStyles}
                                    variant="contained"
                                >
                                    Dismiss
                                </Button>
                            </Grid>
                        </Grid>
                    </Alert>
                    </div>
                </Snackbar>
                <Backdrop
                    sx={backdropStyles}
                    open={backdropOpen}
                />
            </Box>
            {children}
        </>
    )
}

export default VersionAlert
